import { useRoute, useRouter } from 'vue-router';
import { defineStore, storeToRefs } from 'pinia';
import { v4 as uuid } from 'uuid';
import axios from 'axios';
import domToImage from 'dom-to-image';

import Sentry from '../errors/sentry';
// builder-style.store.js imports useBuilder back, but both are Pinia composables only invoked inside
// function bodies (never at module-eval time), so the cycle is inert at runtime.
// eslint-disable-next-line import/no-cycle
import { useBuilderStyles, DOCUMENT_PDF } from './builder-style.store';
import { useUserEvents } from './user-events.store';
import { useNotifications } from './notifications.store';
import { extractContent } from '../utils/helpers';
import { safeForceFlush } from '../utils/tiptap-error-suppression';
import { downloadPdf } from '../api/download';
import { setAuthState } from '../composables/auth';

import { useOnboardingChecklist } from './onboarding-checklist.store.js';
import { usePrimaryResumeStore } from './primary-resume.store';
import { useJobTitleRecommenderStore } from './job-title-recommender.store.js';

import { 
	createResume,
	updateTemplate,
	viewResume,
	retrieveResume,
	updatePreview,
	downloadResume as markResumeDownloaded
} from '../api/builder';

import { 
	createCoverLetter,
	retrieveCoverLetter,
	updatePreview as updatePreviewCoverLetter,
	downloadCoverLetter as markCoverLetterDownloaded
} from '../api/cover-letters';

import {
	BuilderSections,
    
	defaultData,
	getName,
	timer,
	parseBuilderDate,
	parseRetrievedResume,
	getFormattedContact
} from './builder-utils';

// Browsers cap the total body of in-flight keepalive requests at 64 KiB.
const KEEPALIVE_BODY_LIMIT_BYTES = 64 * 1024;
const PREVIEW_DEBOUNCE_MS = 3000;
// EN-1131: dom-to-image never settles when its FileReader fails, and a flush holds a navigation.
const PREVIEW_FLUSH_TIMEOUT_MS = 2000;

const TIMED_OUT = Symbol('timed out');

/** Resolves with `promise`'s value, or TIMED_OUT if it has not settled within `ms`. */
const settleWithin = (promise, ms) => {
	let timeoutId;
	const deadline = new Promise((resolve) => {
		timeoutId = setTimeout(() => resolve(TIMED_OUT), ms);
	});

	return Promise.race([promise, deadline]).finally(() => clearTimeout(timeoutId));
};

const readCookie = (name) => {
	const match = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${name}=`));

	return match ? decodeURIComponent(match.slice(name.length + 1)) : '';
};

const editorInitConfig = {
	isDocumentEditable: true,
	showPersonalInformation: true,
	isDocumentShared: false
};

export const useBuilder = defineStore('pdf-builder', {
	state: () => ({
		_request: null,
		// Autosaves scheduled or in flight. Lets flushSave() know whether anything
		// could still be lost if the page unloads now.
		_pendingSaves: 0,
		_previewTimeout: null,
		activeRoute: null,

		user: null,

		loading: false,
		saved: false,
		// EN-1003: bumped on every completed save. `saved` only flips for the 2s
		// "Saved" badge, so saves inside that window never changed it — watch this
		// instead to react to each one.
		saveCount: 0,
		dragging: false,
		// EN-1136: a save response that would replace `data` while a section is being
		// dragged. Applied when the drag ends — see handleDragEnd().
		_deferredData: null,
		isPreview: false,
		isDocumentEditable: true,
		showPersonalInformation: true,
		isDocumentShared: false,

		id: undefined,
		data: null,

		history: [],
		historyIndex: -1,

		_historyTimeout: null,
		isCoverLetter: false,

		router: useRouter(),
    
		downloadProgress: 0,
		downloadedFileTitle: '',
		showDownloadPopover: false,
		downloadFileObject: {
			progress: 0,
			filename: '',
			error: '',
			uploading: false,
		}
	}),

	actions: {
		async init(id, navigate, activeRoute) {
			this.isDocumentEditable = editorInitConfig.isDocumentEditable;
			this.showPersonalInformation = editorInitConfig.showPersonalInformation;
			this.isDocumentShared = editorInitConfig.isDocumentShared;
    
			this.history = [];
			this.historyIndex = -1;
			this.data = null;
			// A drag cut short by the builder remounting never fires its end event.
			this.dragging = false;
			this._deferredData = null;

			const route = useRoute();

			this.activeRoute = activeRoute;

			const coverLetter = this?.activeRoute?.meta?.coverLetter || false;
			this.isCoverLetter = coverLetter;

			try {
				this.loading = true;
				this.user = setAuthState().getUser();

				const isNew = !id;

				if (isNew) {
					const data = this.buildData({
						// build sections with default values
						...defaultData(coverLetter),
						name: `<b>${getName(this.user)}</b>`,
						contact: getFormattedContact(this.user)
					});

					const response = coverLetter ? await createCoverLetter(data) : await createResume(data);

					// EN-1136: a 2xx without the `data` envelope leaves nothing to open. Why that
					// happens is not yet known, so report it rather than leave a blank builder.
					if (response?.uuid == null) {
						const documentType = coverLetter ? 'cover letter' : 'resume';

						Sentry.captureMessage('builder: create returned no document', {
							level: 'warning',
							tags: { document_type: documentType },
						});
						useNotifications().notify(`We couldn't create your ${documentType}. Please try again.`, 'error');
						navigate({ name: coverLetter ? 'cover-letters.index' : 'resumes' });

						return;
					}

					const userEventsStore = useUserEvents();
					userEventsStore.trackEvent(
						coverLetter
							? userEventsStore.USER_EVENTS.COVER_LETTER_CREATED
							: userEventsStore.USER_EVENTS.RESUME_CREATED, 
						{
							action_id: response.uuid,
							action_name: response.title,
							action_url: coverLetter ? `/cover-letter/${response.uuid}` : `/resume/${response.uuid}`,
						}
					);
           
					if (route?.query.set_primary === 'true' && !coverLetter) {
						await usePrimaryResumeStore().setPrimaryResume(response.uuid, true);
					}
          
					navigate({ path: coverLetter ? `/cover-letter/${response.uuid}` : `/resume/${response.uuid}`, query: { ...route?.query } });
            
					const parsed = coverLetter ? response : parseRetrievedResume(response);

					// apply styles from parsed resume
					const {
						line_height,
						text_size,
						left_margin,
						right_margin,
						font_family,
						top_margin,
						bottom_margin
					} = parsed['options'] || [];

					useBuilderStyles().applyStyles({
						font_family,
						line_height,
						text_size,
						left_margin,
						right_margin,
						top_margin,
						bottom_margin
					});
 
					// apply data 
					this.data = parsed;
					this.id = response.uuid;
				} else {
					try {
						const response = coverLetter
							? await retrieveCoverLetter(id)
							: (this.isPreview ? await viewResume(id) 
								: await retrieveResume(id));

						const parsed = coverLetter ? response : parseRetrievedResume(response);

						// apply styles from parsed resume
						const {
							font_family,
							line_height,
							text_size,
							left_margin,
							right_margin,
							top_margin,
							bottom_margin 
						} = parsed['options'] || [];

						useBuilderStyles().applyStyles({
							font_family,
							line_height,
							text_size,
							left_margin,
							right_margin,
							top_margin,
							bottom_margin
						});

						this.id = id;
						this.data = parsed;
					} catch (e) {
						if (e?.response != null && e?.response?.status === 404) {
							this.id = null;

							this.router.push({
								name: '404',
							});
						}
						return;
					}
				}

				this.user = setAuthState().getUser();
				this.saveHistory();
			} finally {
				this.loading = false;
			}
		},

		initUsingObject(resumeObject, config = editorInitConfig) {
			const { isDocumentEditable, showPersonalInformation, isDocumentShared } = config;
      
			this.isDocumentEditable = isDocumentEditable;
			this.showPersonalInformation = showPersonalInformation;
			this.isDocumentShared = isDocumentShared;

			this.loading = true;

			try {
				const parsed = parseRetrievedResume(resumeObject);

				// apply styles from parsed resume
				const {
					font_family,
					line_height,
					text_size,
					left_margin,
					right_margin
				} = parsed['options'] || [];

				useBuilderStyles().applyStyles({
					font_family,
					line_height,
					text_size,
					left_margin,
					right_margin
				});

				// apply data
				this.data = parsed;
				this.isCoverLetter = false;

				this.user = setAuthState().getUser();
				this.saveHistory();
			} finally {
				this.loading = false;
			}
		},

		resetState() {
			this._request = null;
			this._pendingSaves = 0;
			clearTimeout(this._previewTimeout);
			this._previewTimeout = null;
			this.activeRoute = null;

			this.user = null;

			this.loading = false;
			this.saved = false;
			this.dragging = false;
			this._deferredData = null;
			this.isPreview = false;
			this.isDocumentEditable = true;
			this.showPersonalInformation = true;

			this.id = undefined;
			this.data = null;

			this.history = [];
			this.historyIndex = -1;

			this._historyTimeout = null;
			this.isCoverLetter = false;
		},

		cancelRequest() {
			this._request?.cancel('Canceled');
		},

		handleDragStart() {
			this.dragging = true;
		},
      
		handleDragEnd({ newIndex, oldIndex }) {
			this.dragging = false;

			if (this._deferredData != null) {
				const deferred = this._deferredData;
				this._deferredData = null;

				// The deferred sections are in the order they were saved in; replay the drop on them.
				const sections = deferred.sections ?? [];
				if (newIndex !== oldIndex && sections[oldIndex] != null && newIndex < sections.length) {
					const [moved] = sections.splice(oldIndex, 1);
					sections.splice(newIndex, 0, moved);
				}

				this.data = deferred;
			}

			if ( newIndex !== oldIndex ) {
				this.reorder();
			}
		},

		changeTitle(value) {
			if (this.data?.title == null || this.data?.title === value.value) {return;}
			this.data.title = value || 'Untitled';
			this.save(true);
		},

		changeName(name) {
			this.data.name = (`${  name}`).trim();
			this.save();
		},

		changeContact(contact) {
			this.data.contact = (`${  contact}`).trim();
			this.save();
		},

		changeMessage(message) {
			this.data.message = (`${  message}`).trim();
			this.save();
		},
    
		updateMessage(editor, message) {
			try {
				const bold = message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*\*/g, '');
				const formattedHTML = `<p>${bold.replace(/\n([ \t]*\n)+/g, '</p><p></p><p>').replace(/\n/g, '<br />')}</p>`;
				safeForceFlush(editor);
				editor.commands.setContent(formattedHTML);
				this.save();
			} catch (error) {
				if (!(error instanceof RangeError)) {
					throw error;
				}
			}
		},
		updateSection(editor, message) {
			try {
				const formattedText = message.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*\*/g, '').replace(/\n/g, '<br />');
				safeForceFlush(editor);
				// EN-1137: read the selection after the flush, which can apply pending DOM edits and move it.
				const selection = editor.view.state.selection;
				editor.chain().focus().insertContentAt({
					from: selection.from,
					to: selection.to
				}, formattedText).run();
				this.save();
			} catch (error) {
				if (!(error instanceof RangeError)) {
					throw error;
				}
			}
		},

		changeTemplate(newTemplate) {
			updateTemplate(this.id, newTemplate)
				.then(() => {
					this.data.template = newTemplate;
				});
		},

		// working with section && items
		changeSectionName(sectionId, sectionType, value) {
			const coverLetter = this.isCoverLetter;

			this.data.sections.find((e) => e.id === sectionId)
				.title = (`${  value}`).trim() || defaultData(coverLetter, this.data.template).sections.find((e) => e.type === sectionType).title;

			this.save();
		},
    
		getSectionById(sectionId) {
			return this.data.sections.find((e) => e.id === sectionId);
		},
    
		changeSectionKey(sectionId, sectionType, key, value, itemUuid) {
			const targetSection = this.data.sections.find((e) => e.id === sectionId);
			if (targetSection == null) {return;}

			const coverLetter = this.isCoverLetter;

			// identify item by ID, find index of item
			const itemIndex = targetSection.items.findIndex((item) => item.uuid === itemUuid);

			// we're using section type to get default values
			const formattedValue = typeof value === 'boolean' ? value : ((`${  value}`).trim() || defaultData(coverLetter).sections.find((e) => e.type === sectionType).items[itemIndex][key]);

			if (targetSection != null) {
				targetSection.items[itemIndex][key] = formattedValue;
			}

			this.save();
		},

		changeSectionDate(sectionId, sectionType, key, { date: value, only_year }, itemUuid) {
			const targetSection = this.data.sections.find((e) => e.id === sectionId);
			if (targetSection == null) {return;}

			const coverLetter = this.isCoverLetter;

			const itemIndex = targetSection.items.findIndex((item) => item.uuid === itemUuid);

			const formattedValue = typeof value === 'boolean' ? value : ((`${  value}`).trim() || defaultData(coverLetter).sections.find((e) => e.type === sectionType).items[itemIndex][key]);

			targetSection.items[itemIndex][key] = formattedValue;
			targetSection.items[itemIndex]['only_year'] = only_year;

			this.save();
		},

		changeSectionDateRange(sectionId, { date: value, only_year }, itemUuid, key) {
			const targetSection = this.data.sections.find((e) => e.id === sectionId);
			if (targetSection == null) {return;}

			const itemIndex = targetSection.items.findIndex((item) => item.uuid === itemUuid);

			targetSection.items[itemIndex][key] = value || (key === 'end_at' ? 'Finish Date' : 'Start Date');
			targetSection.items[itemIndex]['only_year'] = only_year;

			this.save();
		},

		changeExperience(itemUuid, val, sectionId, keyToSave = 'bullet_points') {
			const targetSection = this.data.sections.find((e) => e.id === sectionId);
			if (targetSection == null) {return;}

			// identify item by ID, find index of item
			const itemIndex = targetSection.items.findIndex((item) => item.uuid === itemUuid);
			// EN-1020: a caller that omits the uuid used to blow up on items[-1] and lose the edit.
			if (itemIndex === -1) {return;}

			targetSection.items[itemIndex][keyToSave] = val;

			this.save();
		},

		removeSection(sectionId) {
			this.data.sections = this.data.sections
				.filter(({ id }) => id !== sectionId);

			this.save();
		},

		addSection(sectionType, defaultValues = null) {
			const coverLetter = this.isCoverLetter;

			this.data.sections.push({
				...defaultData(coverLetter).sections.find(({ type }) => type === sectionType),
				...defaultValues, ...{ id: Date.now() }
			});

			this.save(false, 10, true);
		},

		removeSectionItem(sectionId, itemUuid) {
			const targetSection = this.data.sections.find((e) => e.id === sectionId);
			if (targetSection == null) {return;}

			const itemIndex = targetSection.items.findIndex((item) => item.uuid === itemUuid);

			const el = targetSection.items[itemIndex] ?? null;
			if ( !el) {return;}

			const els = targetSection.items;

			// Find index of items with id equal to idToDelete
			const indexesToDelete = els.reduce((acc, item, index) => {
				if (item.parent_uuid === el.uuid) {
					acc.push(index);
				}
				return acc;
			}, []);

			indexesToDelete.unshift(itemIndex);

			// Delete items from the array using splice
			indexesToDelete.reverse().forEach(() => {
				targetSection.items.splice(itemIndex, 1);
			});

			this.save();
		},

		async addSectionItem(sectionId, sectionType, defaultParams = {}) {
			const coverLetter = this.isCoverLetter;

			const defaultValue = defaultData(coverLetter)
				.sections
				.find(({ type }) => type === sectionType)
				?.items?.[0] ?? null;

			const totalItems = this.data.sections
				.find(({ id }) => id === sectionId)
				?.items?.length || 0;

			const orderItem = totalItems + 1;

			this.data.sections
				.find(({ id }) => id === sectionId)
				?.items.push({
					...defaultValue , ...defaultParams,
					...{ id: Date.now(), uuid: uuid(), order: orderItem }
				});

			this.save(false, 1500, false);
		},

		reorder() {
			this.data.sections = this.data.sections.map((section, index) => ({
				...section,
				order: index,
			}));

			this.save(false, 1500);
		},

		reorderItems(sectionIndex, items) {
			this.data.sections[sectionIndex].items = items;
			this.save(false, 1500);
		},

		saveHistory() {
			const { fontFamily, textSize, lineHeight, leftMargin, rightMargin } = useBuilderStyles();

			const data = JSON.parse(JSON.stringify({
				...this.data,
				options: {
					font_family: fontFamily,
					text_size: textSize,
					line_height: lineHeight,
					left_margin: leftMargin, 
					right_margin: rightMargin,
				}
			}));

			this.history = this.history.slice(0, this.historyIndex + 1);
			this.history.push(data);
			this.historyIndex = this.historyIndex + 1;
		},

		undo() {
			// Replacing `data` mid-drag tears down the list SortableJS is dragging in.
			if (this.dragging || this.historyIndex <= 0) {
				return;
			}

			this.historyIndex -= 1;
			const data = this.history[this.historyIndex];

			this.afterHistoryAction(data);
		},

		redo() {
			if (this.dragging || this.historyIndex === this.history.length - 1) {
				return;
			}

			this.historyIndex += 1;
			const data = this.history[this.historyIndex];

			this.afterHistoryAction(data);
		},

		afterHistoryAction(data) {
			this.data = JSON.parse(JSON.stringify(data));
			const {
				font_family,
				line_height,
				text_size,
				left_margin,
				right_margin,
				top_margin,
				bottom_margin
			} =  this.data['options'] || [];

			useBuilderStyles().applyStyles({
				font_family,
				text_size,
				line_height,
				left_margin,
				right_margin,
				top_margin,
				bottom_margin
			});

			this.updateAfterHistory();
		},

		updateAfterHistory() {
			clearTimeout(this._historyTimeout);
			this._historyTimeout = setTimeout(() => {
				this.save(true);
			}, 3000);
		},

		/**
		 * EN-1136: `coverLetter` defaults to the type of the loaded document, never the
		 * route — autosaves fire after the user may have navigated away. Callers building
		 * some other document pass its type.
		 */
		buildData(customData, coverLetter = this.isCoverLetter) {
			const data = customData || this.data;

			if (data == null) { return null; }
			const {
				textSize, 
				lineHeight,
				leftMargin,
				rightMargin,
				topMargin,
				bottomMargin,
				fontFamily
			} = useBuilderStyles();

			const builderData = {
				...data,
				id: this.id,
				template: localStorage.getItem('defaultTemplate', 'basic'),
				options: {
					text_size: textSize,
					line_height: lineHeight,
					left_margin: leftMargin,
					top_margin: topMargin,
					bottom_margin: bottomMargin,
					right_margin: rightMargin,
					font_family: fontFamily
				},
			};

			// EN-935: `resume.meta` is server-owned — the score payload, the
			// target selection, the confirmed specialization and the
			// skill-evidence cache all live there and none of them are written
			// by the client. It rides along in `data` because the resume
			// resource returns it, and re-sending that page-load snapshot on
			// every autosave overwrote whatever the server had written since.
			delete builderData.meta;

			// if not cover letter adding section key
			if (! coverLetter) {
				builderData.sections = [
					...data.sections.map((section, index) => ({
						...section,
						order: index,
						...(
							(section.type === BuilderSections.CustomExperience) ? {} : {
								items: section.items.map((item) => ({
									...item,
									...(section.type === BuilderSections.Education ? {
										...parseBuilderDate('date', item),
									} : {
										...parseBuilderDate('start_at', item),
										...parseBuilderDate('end_at', item, true),
									})
								}))
							}
						),
					}))
				];
			}

			return builderData;
		},

		save(ignoreHistory = false, delay = 10, updateState = false, onSucess = null, onError = null) {
			if (!ignoreHistory) {
				this.saveHistory();
			}

			this._request?.cancel('Canceled');

			this._pendingSaves += 1;

			setTimeout(() => {
				if (this.data == null) {
					this._pendingSaves -= 1;

					return;
				}

				this._request = axios.CancelToken.source();

				// Read with id and data at the same moment, so all three describe one document.
				const documentId = this.id;
				const coverLetter = this.isCoverLetter;
				const data = this.buildData(null, coverLetter);

				axios.put(coverLetter ? `/api/cover-letters/${documentId}` : `/api/resumes/${documentId}`, data, {
					cancelToken: this._request.token,
				})
					.finally(() => {
						this._pendingSaves -= 1;
					})
					.then(async ({ data: { data: newData } }) => {
						// The builder may have opened another document while this save was in flight.
						if (updateState && this.id === documentId) {
							const parsed = parseRetrievedResume(newData);

							// New server ids remount sections; doing that mid-drag breaks SortableJS.
							if (this.dragging) {
								this._deferredData = parsed;
							} else {
								this.data = parsed;
							}
						}
            
						// call callable function
						if (onSucess) {onSucess(newData);}

						this.savePreview();
						this.saved = true;
						this.saveCount += 1;
						const jobTitleRecommenderStore = useJobTitleRecommenderStore();
						const primaryResumeStore = usePrimaryResumeStore();

						const { primaryResume } = storeToRefs(primaryResumeStore);
						const { recommendedTitles } = storeToRefs(jobTitleRecommenderStore);

						if (primaryResume.value?.uuid === this.id && recommendedTitles.value.length > 0) {
							jobTitleRecommenderStore.setRecommendedTitles([]);
						}
						await timer(2000);
						this.saved = false;
					})
					.catch((err) => {
						if (onError) {onError(err);}
					});
			}, delay);
		},

		/**
		 * EN-996: the page is unloading (reload, close, navigation). Deferred autosaves
		 * and in-flight axios PUTs die with it, so send the current document once more
		 * on a keepalive request the browser lets finish. Returns whether one was sent.
		 */
		flushSave() {
			if (this._pendingSaves === 0 || this.data == null) { return false; }

			const body = JSON.stringify(this.buildData());
			if (new TextEncoder().encode(body).length > KEEPALIVE_BODY_LIMIT_BYTES) {
				Sentry.captureMessage('builder: document too large for keepalive flush', {
					level: 'warning',
					tags: { document: this.id },
				});

				return false;
			}

			fetch(this.isCoverLetter ? `/api/cover-letters/${this.id}` : `/api/resumes/${this.id}`, {
				method: 'PUT',
				keepalive: true,
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
					'X-XSRF-TOKEN': readCookie('XSRF-TOKEN'),
				},
				body,
			}).catch(() => {});

			return true;
		},

		/**
		 * Every successful autosave asks for a fresh preview image. Now that edits save
		 * on every typing pause, coalesce a burst into one dom-to-image render.
		 */
		savePreview() {
			clearTimeout(this._previewTimeout);
			this._previewTimeout = setTimeout(() => {
				this._previewTimeout = null;
				this.renderPreview();
			}, PREVIEW_DEBOUNCE_MS);
		},

		/**
		 * Render a debounced preview now, while the builder DOM still exists. Called before
		 * leaving the builder so the dashboard thumbnail reflects the last edit.
		 */
		async flushPreview() {
			if (this._previewTimeout == null) { return; }

			clearTimeout(this._previewTimeout);
			this._previewTimeout = null;

			// Callers hold a navigation on this; a failed or hung thumbnail must not block it.
			try {
				await this.renderPreview({ timeoutMs: PREVIEW_FLUSH_TIMEOUT_MS });
			} catch (error) {
				Sentry.captureException(error);
			}
		},

		/**
		 * With `timeoutMs`, a render still running at the deadline is abandoned rather than
		 * uploaded. dom-to-image measures the live node only once fonts are inlined, and by then
		 * the navigation a flush let through has unmounted it, leaving a 0×0 image.
		 */
		async renderPreview({ timeoutMs = null } = {}) {
			// The render outlives navigation, by which time the store may hold another document.
			const { id, isCoverLetter: coverLetter } = this;

			const filter = (node) => node.tagName?.toLowerCase() !== 'img';

			const element = document.getElementById('pdf-resume-builder-content');

			if (element instanceof HTMLElement) {
				const render = domToImage.toBlob(element, {
					filter,
					quality: 1,
				});
				const blob = timeoutMs == null ? await render : await settleWithin(render, timeoutMs);

				if (blob === TIMED_OUT) {
					Sentry.captureMessage('builder: preview render timed out', {
						level: 'warning',
						tags: { document: id },
					});

					return;
				}

				coverLetter ? updatePreviewCoverLetter(id, blob) : updatePreview(id, blob);
			}
		},

		async download(id, format = DOCUMENT_PDF, isCoverLetter) {
			const coverLetter = this?.activeRoute?.meta?.coverLetter || isCoverLetter || false;

			coverLetter 
				? markCoverLetterDownloaded(id || this.id)
				: markResumeDownloaded(id || this.id);

			const resume = coverLetter ? await retrieveCoverLetter(id || this.id) : await retrieveResume(id || this.id);
			const data = coverLetter ? resume :  parseRetrievedResume(resume);

			if (format === DOCUMENT_PDF) {
				this.downloadFileObject.filename = extractContent(data.title);
				this.showDownloadPopover = true;
				this.downloadFileObject.uploading = true;

				try {
					await downloadPdf(
						{
							id: id || this.id,
							isCoverLetter: coverLetter
						},
						extractContent(data.title),
						(percent) => { this.downloadFileObject.progress = percent; }
					);
				}
				catch {
					this.downloadFileObject.error = 'An error occurred. Please try again.';
				}
				finally {
					this.downloadFileObject.uploading = false;
				}
			}
      
			const onboardingChecklistStore = useOnboardingChecklist();
      
			if (coverLetter) {
				onboardingChecklistStore.markItemAsCompleted(2, 'downloadCoverLetter');
			} else {
				onboardingChecklistStore.markItemAsCompleted(1, 'downloadResume');
			}
		},

		resetDownloadState() {
			this.downloadProgress = 0;
			this.downloadedFileTitle = '';
			this.showDownloadPopover = false;
			this.downloadFileObject = {
				progress: 0,
				filename: '',
				error: '',
				uploading: false
			};
		}
	},

	getters: {
		loaded({ loading, data }) {
			return !loading && !!data;
		},
	}
});
